// @ds/config/crud — the write-side twin of `Query<T>`.
//
// Reads are declared via `Query<T>` (schema + filter/sort) and run through the
// dispatcher. Writes need no spec object at all: the schema already owns
// `resource` / `writeResource` / `softDelete` / `invalidateResources`, so the
// schema IS the write spec. `useResourceCrud(schema)` turns it into the three
// canonical mutations, each invalidating the right resources afterwards.
//
//   const crud = useResourceCrud(contactSchema);
//   await crud.create(values);     // useCreate(resource)
//   await crud.update(id, patch);  // useUpdate(writeResource ?? resource)
//   await crud.remove(id);         // soft (is_deleted:true) or hard, per meta
//
// `crudSection(...)` builds the declarative add/edit/delete wiring for a
// row-card sub-resource section from one block (schema + create/edit form
// renderers), so configs stop hand-rolling modal state + onAdd/onRowEdit/
// onRowDelete/outside per section.

import React, { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { App } from "antd";
import type { MenuProps } from "antd";
import { useTranslation } from "react-i18next";
import { useCreate, useDelete, useInvalidate, useUpdate } from "@refinedev/core";
import { getSchemaMeta } from "@ds/config/schema";
import type { AnySchema } from "@ds/config/query";
import {
  confirmRowDelete,
  type ActionContext,
  type RowHandler,
} from "@ds/config/actionContext";
import type { ActionConfig } from "@ds/config/entityConfig";
import { useNavigate } from "@ds/navigation/navigation";
import { SchemaForm } from "@ds/forms/SchemaForm";
import { deleteAction } from "@ds/actions/entityActions";
import type { FormViewConfig } from "@ds/forms/formViewConfig";

/**
 * Inline-edit empty normalization for `fieldEdit`: an empty-after-trim string
 * clears the field to null unless the field opted out via `nonNullableFields`
 * (issue #205 — a whitespace-only edit is the operator clearing the field).
 * Trimming of non-empty values happens once for every write path in
 * `createAppDataProvider` — this only decides empty-vs-null.
 */
export function normalizeFieldEditValue(value: unknown, skipNullify: boolean): unknown {
  return typeof value === "string" && value.trim() === "" && !skipNullify ? null : value;
}

export interface ResourceCrud<T> {
  /** Insert a new row, then invalidate. */
  create: (values: Partial<T>) => Promise<void>;
  /** Patch one row by id, then invalidate. */
  update: (id: number, patch: Partial<T>) => Promise<void>;
  /**
   * EntityViewPage `onFieldEdit` handler factory bound to a record id.
   * Normalizes `"" → null` (except `nonNullableFields`) and patches via
   * `update`, then fires `onSuccess`. Pass the result straight to
   * `sectionContext.onFieldEdit` — no per-entity closure needed.
   */
  fieldEdit: (
    id: number,
    opts?: { nonNullableFields?: ReadonlySet<string>; onSuccess?: () => void },
  ) => (sectionKey: string, fieldKey: string, value: unknown) => Promise<void>;
  /** Remove one row by id — soft (`is_deleted: true`) or hard, per the
   *  schema's `softDelete` meta — then invalidate. */
  remove: (id: number) => Promise<void>;
  /**
   * The standard destructive "delete" `view.moreActions` builder, wired to
   * `remove` + a confirm. `gate` is the per-entity permission (e.g.
   * `canManage`); the action is dropped when it's false or the record has no
   * id. Assign straight to `view.moreActions`.
   */
  deleteMoreActions: (
    gate?: boolean,
    opts?: { confirmTitle?: string; confirmDescription?: string },
  ) => (record: { id?: number }) => ActionConfig[];
}

export function useResourceCrud<T>(
  schema: AnySchema<T>,
  opts?: {
    /** Force hard delete despite the schema's `softDelete` flag. */
    softDelete?: boolean;
    /** Page-level delete: after `remove`, toast "deleted" + navigate here.
     *  Omit for row-level deletes (sub-section rows stay on the page). */
    exitTo?: string;
  },
): ResourceCrud<T> {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const meta = getSchemaMeta(schema);
  const writeResource = meta.writeResource ?? meta.resource;
  // Delete mode defaults to the schema's `softDelete`; callers can force hard
  // delete (e.g. companies — a real row removal despite the soft-delete flag).
  const softDelete = opts?.softDelete ?? !!meta.softDelete;
  const exitTo = opts?.exitTo;

  const targets = useMemo(() => {
    const set = new Set<string>([writeResource, meta.resource]);
    for (const r of meta.invalidateResources ?? []) set.add(r);
    return [...set];
  }, [writeResource, meta.resource, meta.invalidateResources]);

  const { mutateAsync: createMut } = useCreate();
  const { mutateAsync: updateMut } = useUpdate();
  const { mutateAsync: deleteMut } = useDelete();
  const invalidate = useInvalidate();

  // Refine builds the detail query key from `(resource, "one", id)`, so the id
  // is REQUIRED for `["detail"]` to hit a real cache entry. Omit it on create
  // (no id yet) — only the list invalidation matters there.
  const refresh = useCallback(
    (id?: number) =>
      Promise.all(
        targets.map((resource) =>
          invalidate({ resource, invalidates: ["list", "detail"], id }),
        ),
      ),
    [invalidate, targets],
  );

  const create = useCallback(
    async (values: Partial<T>) => {
      await createMut({ resource: writeResource, values, successNotification: false });
      await refresh();
    },
    [createMut, writeResource, refresh],
  );

  const update = useCallback(
    async (id: number, patch: Partial<T>) => {
      await updateMut({ resource: writeResource, id, values: patch, successNotification: false });
      await refresh(id);
    },
    [updateMut, writeResource, refresh],
  );

  const fieldEdit = useCallback(
    (
      id: number,
      fopts?: { nonNullableFields?: ReadonlySet<string>; onSuccess?: () => void },
    ) =>
      async (_sectionKey: string, fieldKey: string, value: unknown) => {
        const skipNullify = fopts?.nonNullableFields?.has(fieldKey) ?? false;
        const normalized = normalizeFieldEditValue(value, skipNullify);
        await update(id, { [fieldKey]: normalized } as Partial<T>);
        fopts?.onSuccess?.();
      },
    [update],
  );

  const remove = useCallback(
    async (id: number) => {
      if (softDelete) {
        await updateMut({
          resource: writeResource,
          id,
          values: { is_deleted: true },
          successNotification: false,
        });
      } else {
        await deleteMut({ resource: writeResource, id, successNotification: false });
      }
      await refresh(id);
      // Page-level delete: announce + leave the (now-gone) record's page.
      if (exitTo) {
        message.success(t("common.messages.deleted", "Usunięto"));
        navigate(exitTo);
      }
    },
    [updateMut, deleteMut, writeResource, softDelete, refresh, exitTo, message, navigate, t],
  );

  const deleteMoreActions = useCallback(
    (gate = true, dopts?: { confirmTitle?: string; confirmDescription?: string }) =>
      (record: { id?: number }): ActionConfig[] => {
        const id = record.id;
        return id != null && gate
          ? [
              deleteAction({
                label: t("common.actions.delete"),
                confirmTitle: dopts?.confirmTitle ?? t("common.confirm_delete"),
                confirmDescription: dopts?.confirmDescription,
                onClick: () => void remove(id),
              }),
            ]
          : [];
      },
    [remove, t],
  );

  return { create, update, fieldEdit, remove, deleteMoreActions };
}

// ─── crudSection — declarative add/edit/delete for a row-card section ────────
//
// Section-scoped state (which modal is open) + the schema CRUD bundle. The
// SchemaForm modals run through Refine's `useModalForm`, which auto-invalidates
// the resource on save, so the list refetches without a `saved` callback.
export type SectionCrudState<TRow> = {
  createOpen: boolean;
  setCreateOpen: (v: boolean) => void;
  editId: number | null;
  setEditId: (v: number | null) => void;
} & ResourceCrud<TRow>;

export interface SectionCrudConfig<
  TParent extends { id?: number },
  TRow extends { id: number },
  Extra = unknown,
> {
  /** Drives delete (soft/hard) + invalidation. Usually the same schema the
   *  section's `query` uses. */
  schema: AnySchema<TRow>;
  /**
   * Declarative create+edit form. For simple sub-resources whose form is just
   * a `<SchemaForm>`, crudSection renders it for both modes — no wrapper
   * component needed. Overridden by `createForm` / `editForm` when a form
   * needs bespoke logic (async validation, file upload, pickers).
   */
  form?: {
    /** SchemaForm view. `schema` is injected from `cfg.schema` — omit it
     *  here; `resource`/`entityName` default to the schema's meta too, so the
     *  inline view is usually just `titleOverride` + `sections`. */
    view: Omit<FormViewConfig<TRow>, "schema">;
    /** FK on TRow set to `parent.id` in create mode (e.g. `"company_id"`). */
    parentField?: string;
    /** Field-testid prefix (defaults to SchemaForm's `${resource}-${mode}-form`). */
    testIdPrefix?: string;
  };
  /** Create-modal escape — overrides `form` for create. */
  createForm?: (parent: TParent, close: () => void) => ReactNode;
  /** Edit-modal escape — overrides `form` for edit. */
  editForm?: (parent: TParent, id: number, close: () => void) => ReactNode;
  /** Delete-confirm copy overrides. */
  confirm?: { titleKey?: string; okKey?: string };
  /**
   * Section-scoped extension escape — for sub-resource sections that need
   * MORE than add/edit/delete (e.g. the bank-accounts status lifecycle).
   *   - `useState` — extra state hook composed INTO the section state, so
   *     every slot below (and the section's own callbacks) sees
   *     `SectionCrudState<TRow> & Extra`.
   *   - `outside`  — extra sibling nodes (modals/drawers) rendered AFTER the
   *     base create/edit modals.
   *   - `rowMenu`  — per-row overflow menu items, forwarded verbatim to the
   *     row-card layout's `rowMenu` slot via `.row`.
   */
  extra?: {
    useState?: () => Extra;
    outside?: (parent: TParent, state: SectionCrudState<TRow> & Extra) => ReactNode;
    rowMenu?: (
      row: TRow,
      ctx: ActionContext,
      state: SectionCrudState<TRow> & Extra,
    ) => MenuProps["items"];
  };
}

export interface SectionCrudPieces<
  TParent extends { id?: number },
  TRow extends { id: number },
  Extra = unknown,
> {
  /** Spread into the `DataSection` (`useSectionState` + `onAdd` + `outside`). */
  section: {
    useSectionState: () => SectionCrudState<TRow> & Extra;
    onAdd?: (s: SectionCrudState<TRow> & Extra) => void;
    outside: (parent: TParent, s: SectionCrudState<TRow> & Extra) => ReactNode;
  };
  /** Generic row behaviors — spread into ANY data layout that accepts them
   *  (the row-card layout's `onRowEdit` / `onRowDelete` / `rowMenu`). Layout
   *  choice + field placement stay with the section. */
  row: {
    onRowEdit?: RowHandler<TRow, SectionCrudState<TRow> & Extra>;
    onRowDelete: RowHandler<TRow, SectionCrudState<TRow> & Extra>;
    rowMenu?: (
      row: TRow,
      ctx: ActionContext,
      state: SectionCrudState<TRow> & Extra,
    ) => MenuProps["items"];
  };
}

export function crudSection<
  TParent extends { id?: number },
  TRow extends { id: number },
  Extra = unknown,
>(
  cfg: SectionCrudConfig<TParent, TRow, Extra>,
): SectionCrudPieces<TParent, TRow, Extra> {
  // Resolve the create/edit renderers: explicit escapes win; otherwise derive
  // a plain <SchemaForm> from the declarative `form` spec, injecting the
  // section's schema into the view.
  const form = cfg.form;
  const formView = form
    ? ({ ...form.view, schema: cfg.schema } as FormViewConfig<TRow>)
    : undefined;

  const createForm: ((parent: TParent, close: () => void) => ReactNode) | undefined =
    cfg.createForm ??
    (formView
      ? (parent, close) =>
          parent.id != null ? (
            <SchemaForm<TRow>
              view={formView}
              mode="create"
              open
              onClose={close}
              parentValues={form?.parentField ? { [form.parentField]: parent.id } : undefined}
              formTestIdPrefix={form?.testIdPrefix}
            />
          ) : null
      : undefined);

  const editForm: ((parent: TParent, id: number, close: () => void) => ReactNode) | undefined =
    cfg.editForm ??
    (formView
      ? (_parent, id, close) => (
          <SchemaForm<TRow>
            view={formView}
            mode="edit"
            id={id}
            open
            onClose={close}
            formTestIdPrefix={form?.testIdPrefix}
          />
        )
      : undefined);

  // `cfg.extra` is fixed per crudSection call, so the conditional extra-state
  // call is hook-order-stable across renders (same trick as renderSection's
  // `useResolvedSectionState` — local non-`use` alias keeps the linter calm).
  const extraStateHook = cfg.extra?.useState;

  const useSectionState = (): SectionCrudState<TRow> & Extra => {
    const [createOpen, setCreateOpen] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const extraState = extraStateHook ? extraStateHook() : ({} as Extra);
    return {
      createOpen,
      setCreateOpen,
      editId,
      setEditId,
      ...useResourceCrud(cfg.schema),
      ...extraState,
    };
  };

  return {
    section: {
      useSectionState,
      onAdd: createForm ? (s) => s.setCreateOpen(true) : undefined,
      outside: (parent, s) => (
        <>
          {createForm && s.createOpen ? createForm(parent, () => s.setCreateOpen(false)) : null}
          {editForm && s.editId != null ? editForm(parent, s.editId, () => s.setEditId(null)) : null}
          {cfg.extra?.outside?.(parent, s)}
        </>
      ),
    },
    row: {
      onRowEdit: editForm ? (row, _ctx, s) => s.setEditId(row.id) : undefined,
      onRowDelete: confirmRowDelete<TRow, SectionCrudState<TRow> & Extra>(
        (row, s) => s.remove(row.id),
        cfg.confirm,
      ),
      rowMenu: cfg.extra?.rowMenu,
    },
  };
}
